import { doc, getDoc, increment, setDoc } from "firebase/firestore";
import { db } from "./firebase/config.js";

// Estadísticas del menú público: un doc por negocio y por día en stats/{usuarioEmpresa}_{fecha}.
// Lo escribe cualquier visitante (sumando, ver firestore.rules) y lo lee solo el dueño.
//
// Campos del doc:
// - visitas, visitasQr: visitas al menú y cuántas entraron escaneando el QR (?o=qr).
// - horas.{0-23}, horasPedidos.{0-23}: visitas y pedidos según la hora del día.
// - vistas.{productId}: veces que se abrió el detalle de cada producto.
// - exploraron: visitas que abrieron al menos un producto.
// - carritos: visitas que agregaron algo al pedido. agregados.{productId}: visitas que agregaron ese producto.
// - pedidos: pedidos enviados por WhatsApp. enviados.{productId}: unidades pedidas de cada producto.
// - importe y pedidosConTotal: suma de los totales (los pedidos con algún precio no numérico no suman).
// - retiro, envio: forma de entrega elegida en el pedido.
// - clicsWhatsapp: toques en el botón de WhatsApp de la cabecera del menú.

const pad = (n) => String(n).padStart(2, "0");
export const dayKey = (date = new Date()) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

// Desde este día se miden carritos, horarios, origen, entrega y clicks en WhatsApp.
export const DETAILED_STATS_SINCE = "2026-10-30";

// Parámetro que llevan los QR y carteles para saber que la visita vino de escanear.
export const QR_PARAM = "o";
export const QR_VALUE = "qr";

const statRef = (usuarioEmpresa, fecha) => doc(db, "stats", `${usuarioEmpresa}_${fecha}`);

// Cada cosa se cuenta una vez por visita (por pestaña), para no inflar con recargas.
const firstTime = (key) => {
    try {
        if (sessionStorage.getItem(key)) return false;
        sessionStorage.setItem(key, "1");
    } catch (error) {
        // Sin almacenamiento se cuenta igual.
    }
    return true;
}

const bump = (business, fields) => {
    const fecha = dayKey();
    // Un error acá (sin conexión, reglas sin publicar) no tiene que molestar al cliente.
    setDoc(statRef(business.usuarioEmpresa, fecha), {
        id: business.id,
        usuarioEmpresa: business.usuarioEmpresa,
        fecha,
        ...fields
    }, { merge: true }).catch(() => {});
}

const hour = () => String(new Date().getHours());
const once = (business, what) => firstTime(`miqrapp-${what}-${business.usuarioEmpresa}`);

export const trackVisit = (business, fromQr) => {
    if (!once(business, "visita")) return;
    bump(business, {
        visitas: increment(1),
        horas: { [hour()]: increment(1) },
        ...(fromQr && { visitasQr: increment(1) })
    });
}

export const trackProductView = (business, productId) => {
    if (!once(business, "vista-" + productId)) return;
    bump(business, {
        vistas: { [productId]: increment(1) },
        ...(once(business, "exploro") && { exploraron: increment(1) })
    });
}

export const trackAddToCart = (business, productId) => {
    if (!once(business, "agregado-" + productId)) return;
    bump(business, {
        agregados: { [productId]: increment(1) },
        ...(once(business, "carrito") && { carritos: increment(1) })
    });
}

// Las reglas aceptan hasta 50 productos distintos por pedido.
export const trackOrder = (business, lines, total, entrega) => {
    const sent = Object.fromEntries(lines.slice(0, 50).map((l) => [l.product.id, increment(l.qty)]));
    bump(business, {
        pedidos: increment(1),
        horasPedidos: { [hour()]: increment(1) },
        enviados: sent,
        // Un pedido con el carrito de una visita anterior (guardado en el navegador) también armó carrito.
        ...(once(business, "carrito") && { carritos: increment(1) }),
        ...(total !== null && total > 0 && total < 10000000 && { importe: increment(total), pedidosConTotal: increment(1) }),
        ...((entrega === "retiro" || entrega === "envio") && { [entrega]: increment(1) })
    });
}

export const trackWhatsappClick = (business) => {
    if (once(business, "whatsapp")) bump(business, { clicsWhatsapp: increment(1) });
}

const COUNTERS = ["visitas", "visitasQr", "exploraron", "carritos", "pedidos", "pedidosConTotal", "importe", "retiro", "envio", "clicsWhatsapp"];
const MAPS = ["vistas", "agregados", "enviados", "horas", "horasPedidos"];

const daysBack = (days, offset = 0) => Array.from({ length: days }, (_, i) => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - offset - (days - 1 - i));
    return d;
});

// Últimos `days` días, del más viejo al de hoy (con `offset`, los `days` anteriores a esos).
// Los días sin visitas no tienen doc y vuelven en cero.
export const loadStats = async (usuarioEmpresa, days, offset = 0) => {
    const dates = daysBack(days, offset);
    const snaps = await Promise.all(dates.map((d) => getDoc(statRef(usuarioEmpresa, dayKey(d)))));
    return snaps.map((snap, i) => {
        const data = snap.data() || {};
        const day = { date: dates[i] };
        COUNTERS.forEach((key) => { day[key] = Number(data[key]) || 0; });
        MAPS.forEach((key) => { day[key] = data[key] && typeof data[key] === "object" ? data[key] : {}; });
        return day;
    });
}
